import { useState, type ReactNode } from 'react';
import {
  createRumSchema,
  RUM_TYPE_LABELS,
  RUM_TYPES,
  type CreateRumInput,
  type RumType,
} from '@cercle/shared';
import { ReferenceCombo } from '@/shared/reference/ReferenceCombo';
import { Field, Input, Segmented } from '@/shared/ui';
import styles from './rum-fields.module.scss';

/**
 * Les champs d'une fiche de rhum, partagés par la création (écran 09) et la correction.
 *
 * Ils vivaient dans `NewRumScreen`. Les recopier dans l'écran de modification aurait fait
 * deux formulaires qui divergent à la première évolution — un champ ajouté à la création et
 * absent de la correction se remarque des mois plus tard, quand un maître de chai ne trouve
 * pas ce qu'il cherche.
 *
 * `price` n'est délibérément pas saisi ici : il existe en base et dans le contrat, mais aucune
 * maquette ne le demande, et l'ajouter au formulaire serait décider à la place du cercle.
 */
export interface RumFieldsState {
  name: string;
  setName: (value: string) => void;
  rumType: RumType;
  setRumType: (value: RumType) => void;
  distillery: string;
  setDistillery: (value: string) => void;
  country: string;
  setCountry: (value: string) => void;
  style: string;
  setStyle: (value: string) => void;
  abv: string;
  setAbv: (value: string) => void;
  age: string;
  setAge: (value: string) => void;
  barcode: string;
  setBarcode: (value: string) => void;
  /**
   * La fiche telle qu'elle sera envoyée, ou un message si la saisie ne passe pas.
   *
   * La validation est faite par `createRumSchema`, celui-là même dont l'API se sert : deux
   * validations écrites séparément finiraient par ne plus dire la même chose, et c'est
   * précisément ce que `packages/shared` existe pour éviter.
   */
  build: () => { ok: true; value: CreateRumInput } | { ok: false; message: string };
}

/** Ce qui préremplit le formulaire à la correction. Vide à la création. */
export interface RumFieldsInitial {
  name?: string;
  rumType?: RumType | null;
  distillery?: string | null;
  country?: string | null;
  style?: string | null;
  abv?: number | null;
  age?: number | null;
  barcode?: string | null;
}

export function useRumFields(initial?: RumFieldsInitial): RumFieldsState {
  const [name, setName] = useState(initial?.name ?? '');
  const [rumType, setRumType] = useState<RumType>(initial?.rumType ?? 'AGRICOLE');
  const [distillery, setDistillery] = useState(initial?.distillery ?? '');
  const [country, setCountry] = useState(initial?.country ?? '');
  const [style, setStyle] = useState(initial?.style ?? '');
  // Les nombres sont tenus en texte : un champ à moitié tapé (« 62, ») n'est pas un nombre,
  // et le convertir à chaque frappe rendrait la virgule impossible à saisir.
  const [abv, setAbv] = useState(initial?.abv === null || initial?.abv === undefined ? '' : String(initial.abv));
  const [age, setAge] = useState(initial?.age === null || initial?.age === undefined ? '' : String(initial.age));
  const [barcode, setBarcode] = useState(initial?.barcode ?? '');

  function build(): { ok: true; value: CreateRumInput } | { ok: false; message: string } {
    // Un champ vide devient `null` et non `""` : côté API, `undefined` laisse la valeur
    // inchangée alors que `null` l'efface — la distinction est ce qui permet de retirer
    // une information erronée d'une fiche, et pas seulement d'en ajouter.
    const parsed = createRumSchema.safeParse({
      name,
      rumType,
      distillery: distillery.trim() || null,
      country: country.trim() || null,
      style: style.trim() || null,
      abv: abv ? Number(abv.replace(',', '.')) : null,
      age: age ? Number(age) : null,
      barcode: barcode.trim() || null,
    });

    if (!parsed.success) {
      return { ok: false, message: parsed.error.issues[0]?.message ?? 'Fiche invalide.' };
    }
    return { ok: true, value: parsed.data };
  }

  return {
    name,
    setName,
    rumType,
    setRumType,
    distillery,
    setDistillery,
    country,
    setCountry,
    style,
    setStyle,
    abv,
    setAbv,
    age,
    setAge,
    barcode,
    setBarcode,
    build,
  };
}

export function RumFields({ fields }: { fields: RumFieldsState }): ReactNode {
  return (
    <>
      <div className={styles.grid}>
        <Field label="Nom *">
          <Input
            value={fields.name}
            onChange={(event) => fields.setName(event.target.value)}
            placeholder="Long Pond TECA 2007"
            required
          />
        </Field>

        <Field label="Distillerie">
          <Input
            value={fields.distillery}
            onChange={(event) => fields.setDistillery(event.target.value)}
            placeholder="Long Pond"
          />
        </Field>

        <Field label="Pays" hint="Absent de la liste ? Ajoutez-le.">
          <ReferenceCombo
            kind="COUNTRY"
            value={fields.country}
            onChange={fields.setCountry}
            placeholder="Jamaïque"
          />
        </Field>

        <Field label="Degré">
          <Input
            inputMode="decimal"
            value={fields.abv}
            onChange={(event) => fields.setAbv(event.target.value)}
            placeholder="62,5"
          />
        </Field>

        <Field label="Âge" hint="En années.">
          <Input
            inputMode="numeric"
            value={fields.age}
            onChange={(event) => fields.setAge(event.target.value)}
            placeholder="8"
          />
        </Field>

        <Field label="Code-barres" hint="Conservé pour un rapprochement ultérieur.">
          <Input
            inputMode="numeric"
            value={fields.barcode}
            onChange={(event) => fields.setBarcode(event.target.value)}
            placeholder="3801227400613"
          />
        </Field>
      </div>

      <fieldset className={styles.group}>
        <legend>Type *</legend>
        <Segmented
          label="Type de rhum"
          value={fields.rumType}
          onChange={fields.setRumType}
          options={RUM_TYPES.map((value) => ({ value, label: RUM_TYPE_LABELS[value] }))}
        />
      </fieldset>

      <fieldset className={styles.group}>
        <legend>Style</legend>
        <ReferenceCombo
          kind="STYLE"
          value={fields.style}
          onChange={fields.setStyle}
          placeholder="Grand arôme"
        />
      </fieldset>
    </>
  );
}
